import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Grid,
  Tabs,
  Tab,
  Chip,
  FormControlLabel,
  Switch,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  RestoreFromTrash as RestoreIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { SmtpSetting, SmtpSettingCreate, SmtpSettingUpdate } from '../../../types/smtpSetting';
import { smtpSettingsAPI } from '../../../services/api';
import { getModuleTableTheme } from '../../../theme/tableTheme';
import AlertDialog from '../../../components/AlertDialog';
import useAlert from '../../../hooks/useAlert';
import { extractErrorMessage } from '../../../utils/errorHandler';

const SmtpSettingsManagement: React.FC = () => {
  const [settings, setSettings] = useState<SmtpSetting[]>([]);
  const [deletedSettings, setDeletedSettings] = useState<SmtpSetting[]>([]);
  const [open, setOpen] = useState(false);
  const [testEmailOpen, setTestEmailOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingSetting, setEditingSetting] = useState<SmtpSetting | null>(null);
  const [currentSetting, setCurrentSetting] = useState<Partial<SmtpSettingCreate>>({
    name: '',
    host: '',
    port: 587,
    username: '',
    password: '',
    from_email: '',
    from_name: '',
    use_tls: true,
    use_ssl: false,
    is_active: true,
    is_default: false,
  });
  const [testEmail, setTestEmail] = useState('');
  const [testingSettingId, setTestingSettingId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const { alertState, showError, showSuccess, showConfirm, hideAlert } = useAlert();

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const data = await smtpSettingsAPI.getAll();
      setSettings(data);
    } catch (error) {
      showError('Erreur lors du chargement des configurations SMTP');
      console.error('Error fetching SMTP settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDeletedSettings = async () => {
    try {
      setLoading(true);
      const data = await smtpSettingsAPI.getDeleted();
      setDeletedSettings(data);
    } catch (error) {
      showError('Erreur lors du chargement des configurations supprimées');
      console.error('Error fetching deleted SMTP settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (setting: SmtpSetting) => {
    try {
      setLoading(true);
      await smtpSettingsAPI.restore(setting.id);
      showSuccess('Configuration SMTP restaurée avec succès');
      fetchDeletedSettings();
      fetchSettings();
    } catch (error) {
      showError('Erreur lors de la restauration');
      console.error('Error restoring SMTP setting:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    if (newValue === 1) {
      fetchDeletedSettings();
    }
  };

  const handleOpen = (setting?: SmtpSetting) => {
    if (setting) {
      setEditingSetting(setting);
      setCurrentSetting({
        name: setting.name,
        host: setting.host,
        port: setting.port,
        username: setting.username,
        password: '', // Don't show existing password
        from_email: setting.from_email,
        from_name: setting.from_name,
        use_tls: setting.use_tls,
        use_ssl: setting.use_ssl,
        is_active: setting.is_active,
        is_default: setting.is_default,
      });
      setEditMode(true);
    } else {
      setEditingSetting(null);
      setCurrentSetting({
        name: '',
        host: '',
        port: 587,
        username: '',
        password: '',
        from_email: '',
        from_name: '',
        use_tls: true,
        use_ssl: false,
        is_active: true,
        is_default: false,
      });
      setEditMode(false);
    }
    setShowPassword(false);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingSetting(null);
    setShowPassword(false);
  };

  const handleSave = async () => {
    if (!currentSetting.name?.trim() || !currentSetting.host?.trim()) {
      showError('Le nom et le serveur sont obligatoires');
      return;
    }
    if (!editMode && !currentSetting.password?.trim()) {
      showError('Le mot de passe est obligatoire');
      return;
    }

    try {
      setLoading(true);
      if (editMode && editingSetting) {
        const updateData: SmtpSettingUpdate = {
          name: currentSetting.name,
          host: currentSetting.host,
          port: currentSetting.port,
          username: currentSetting.username,
          from_email: currentSetting.from_email,
          from_name: currentSetting.from_name,
          use_tls: currentSetting.use_tls,
          use_ssl: currentSetting.use_ssl,
          is_active: currentSetting.is_active,
          is_default: currentSetting.is_default,
        };
        // Only include password if provided
        if (currentSetting.password?.trim()) {
          updateData.password = currentSetting.password;
        }
        await smtpSettingsAPI.update(editingSetting.id, updateData);
        showSuccess('Configuration SMTP modifiée avec succès');
      } else {
        const createData: SmtpSettingCreate = {
          name: currentSetting.name!,
          host: currentSetting.host!,
          port: currentSetting.port!,
          username: currentSetting.username!,
          password: currentSetting.password!,
          from_email: currentSetting.from_email!,
          from_name: currentSetting.from_name!,
          use_tls: currentSetting.use_tls,
          use_ssl: currentSetting.use_ssl,
          is_active: currentSetting.is_active,
          is_default: currentSetting.is_default,
        };
        await smtpSettingsAPI.create(createData);
        showSuccess('Configuration SMTP créée avec succès');
      }
      await fetchSettings();
      handleClose();
    } catch (err: any) {
      showError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (setting: SmtpSetting) => {
    showConfirm(
      `Êtes-vous sûr de vouloir supprimer la configuration "${setting.name}" ?`,
      async () => {
        try {
          setLoading(true);
          await smtpSettingsAPI.delete(setting.id);
          showSuccess('Configuration SMTP supprimée avec succès');
          await fetchSettings();
        } catch (error) {
          showError('Erreur lors de la suppression');
          console.error('Error deleting SMTP setting:', error);
        } finally {
          setLoading(false);
        }
      }
    );
  };

  const handleTestConnection = async (setting: SmtpSetting) => {
    try {
      setTesting(true);
      setTestingSettingId(setting.id);
      const result = await smtpSettingsAPI.testConnection(setting.id);
      if (result.success) {
        showSuccess(result.message);
      } else {
        showError(result.message);
      }
    } catch (err: any) {
      showError(extractErrorMessage(err));
    } finally {
      setTesting(false);
      setTestingSettingId(null);
    }
  };

  const handleOpenTestEmail = (setting: SmtpSetting) => {
    setTestingSettingId(setting.id);
    setTestEmail('');
    setTestEmailOpen(true);
  };

  const handleSendTestEmail = async () => {
    if (!testEmail.trim()) {
      showError('Veuillez entrer une adresse email');
      return;
    }
    if (!testingSettingId) return;

    try {
      setTesting(true);
      const result = await smtpSettingsAPI.sendTestEmail(testingSettingId, { test_email: testEmail });
      if (result.success) {
        showSuccess(result.message);
        setTestEmailOpen(false);
      } else {
        showError(result.message);
      }
    } catch (err: any) {
      showError(extractErrorMessage(err));
    } finally {
      setTesting(false);
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5">Configuration des Serveurs SMTP</Typography>
        {tabValue === 0 && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpen()}
          >
            Nouveau Serveur SMTP
          </Button>
        )}
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Serveurs Actifs" />
          <Tab label="Corbeille" />
        </Tabs>
      </Box>

      {tabValue === 0 && (
        <Box sx={getModuleTableTheme('configuration')}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nom</TableCell>
                  <TableCell>Serveur</TableCell>
                  <TableCell>Expéditeur</TableCell>
                  <TableCell>Statut</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {settings.map((setting) => (
                  <TableRow key={setting.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {setting.is_default && (
                          <StarIcon sx={{ color: 'warning.main', fontSize: 18 }} />
                        )}
                        {setting.name}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {setting.host}:{setting.port}
                      <Typography variant="caption" display="block" color="text.secondary">
                        {setting.use_tls ? 'TLS' : setting.use_ssl ? 'SSL' : 'Non sécurisé'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {setting.from_name}
                      <Typography variant="caption" display="block" color="text.secondary">
                        {setting.from_email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={setting.is_active ? 'Actif' : 'Inactif'}
                        color={setting.is_active ? 'success' : 'default'}
                        size="small"
                      />
                      {setting.is_default && (
                        <Chip
                          label="Par défaut"
                          color="primary"
                          size="small"
                          sx={{ ml: 1 }}
                        />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        onClick={() => handleTestConnection(setting)}
                        color="success"
                        title="Tester la connexion"
                        disabled={testing && testingSettingId === setting.id}
                      >
                        {testing && testingSettingId === setting.id ? (
                          <CircularProgress size={20} />
                        ) : (
                          <CheckCircleIcon />
                        )}
                      </IconButton>
                      <IconButton
                        onClick={() => handleOpenTestEmail(setting)}
                        color="info"
                        title="Envoyer un email de test"
                      >
                        <SendIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handleOpen(setting)}
                        color="primary"
                        title="Modifier"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDelete(setting)}
                        color="error"
                        title="Supprimer"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {settings.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      Aucune configuration SMTP trouvée
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {tabValue === 1 && (
        <Box sx={getModuleTableTheme('configuration')}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nom</TableCell>
                  <TableCell>Serveur</TableCell>
                  <TableCell>Expéditeur</TableCell>
                  <TableCell>Date de Suppression</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {deletedSettings.map((setting) => (
                  <TableRow key={setting.id}>
                    <TableCell>{setting.name}</TableCell>
                    <TableCell>{setting.host}:{setting.port}</TableCell>
                    <TableCell>{setting.from_email}</TableCell>
                    <TableCell>
                      {setting.deleted_at ? new Date(setting.deleted_at).toLocaleDateString('fr-FR') : '-'}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        onClick={() => {
                          showConfirm(
                            `Êtes-vous sûr de vouloir restaurer "${setting.name}" ?`,
                            () => handleRestore(setting),
                            {
                              title: 'Confirmer la restauration',
                              confirmText: 'Restaurer',
                              cancelText: 'Annuler',
                            }
                          );
                        }}
                        color="primary"
                        title="Restaurer"
                      >
                        <RestoreIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {deletedSettings.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      Aucune configuration supprimée
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{editMode ? 'Modifier la Configuration SMTP' : 'Nouvelle Configuration SMTP'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                variant="filled"
                fullWidth
                label="Nom"
                value={currentSetting.name || ''}
                onChange={(e) => setCurrentSetting({ ...currentSetting, name: e.target.value })}
                required
                placeholder="Ex: Support, Marketing"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={currentSetting.is_default ?? false}
                    onChange={(e) => setCurrentSetting({ ...currentSetting, is_default: e.target.checked })}
                  />
                }
                label="Serveur par défaut"
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                variant="filled"
                fullWidth
                label="Serveur SMTP"
                value={currentSetting.host || ''}
                onChange={(e) => setCurrentSetting({ ...currentSetting, host: e.target.value })}
                required
                placeholder="Ex: smtp.gmail.com"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                variant="filled"
                fullWidth
                label="Port"
                type="number"
                value={currentSetting.port || 587}
                onChange={(e) => setCurrentSetting({ ...currentSetting, port: parseInt(e.target.value) })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                variant="filled"
                fullWidth
                label="Nom d'utilisateur"
                value={currentSetting.username || ''}
                onChange={(e) => setCurrentSetting({ ...currentSetting, username: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                variant="filled"
                fullWidth
                label="Mot de passe"
                type={showPassword ? 'text' : 'password'}
                value={currentSetting.password || ''}
                onChange={(e) => setCurrentSetting({ ...currentSetting, password: e.target.value })}
                required={!editMode}
                placeholder={editMode ? 'Laisser vide pour ne pas modifier' : ''}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                variant="filled"
                fullWidth
                label="Email expéditeur"
                type="email"
                value={currentSetting.from_email || ''}
                onChange={(e) => setCurrentSetting({ ...currentSetting, from_email: e.target.value })}
                required
                placeholder="Ex: support@example.com"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                variant="filled"
                fullWidth
                label="Nom expéditeur"
                value={currentSetting.from_name || ''}
                onChange={(e) => setCurrentSetting({ ...currentSetting, from_name: e.target.value })}
                required
                placeholder="Ex: Support MAF"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={currentSetting.use_tls ?? true}
                    onChange={(e) => setCurrentSetting({
                      ...currentSetting,
                      use_tls: e.target.checked,
                      use_ssl: e.target.checked ? false : currentSetting.use_ssl
                    })}
                  />
                }
                label="Utiliser TLS"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={currentSetting.use_ssl ?? false}
                    onChange={(e) => setCurrentSetting({
                      ...currentSetting,
                      use_ssl: e.target.checked,
                      use_tls: e.target.checked ? false : currentSetting.use_tls
                    })}
                  />
                }
                label="Utiliser SSL"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={currentSetting.is_active ?? true}
                    onChange={(e) => setCurrentSetting({ ...currentSetting, is_active: e.target.checked })}
                  />
                }
                label="Actif"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Annuler</Button>
          <Button onClick={handleSave} variant="contained" disabled={loading}>
            {editMode ? 'Modifier' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Test Email Dialog */}
      <Dialog open={testEmailOpen} onClose={() => setTestEmailOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Envoyer un Email de Test</DialogTitle>
        <DialogContent>
          <TextField
            variant="filled"
            fullWidth
            label="Adresse email de test"
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            sx={{ mt: 2 }}
            placeholder="Ex: votre-email@example.com"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestEmailOpen(false)}>Annuler</Button>
          <Button
            onClick={handleSendTestEmail}
            variant="contained"
            disabled={testing}
            startIcon={testing ? <CircularProgress size={20} /> : <SendIcon />}
          >
            Envoyer
          </Button>
        </DialogActions>
      </Dialog>

      <AlertDialog
        open={alertState.open}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        onClose={hideAlert}
        onConfirm={alertState.onConfirm}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
        showCancel={alertState.showCancel}
      />
    </Box>
  );
};

export default SmtpSettingsManagement;
